import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useBranding, applyBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Plug, Palette, Globe, CalendarRange, Calculator, Upload, Check, Copy, ExternalLink, AlertTriangle, Trash2, Database } from "lucide-react";
import { toast } from "sonner";

function CopyBox({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* ignore */ }
  };
  return (
    <div className="mt-2">
      {label && <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>}
      <div className="flex items-stretch border border-border">
        <code className="flex-1 px-3 py-2 text-xs font-mono bg-secondary/50 break-all">{value}</code>
        <button type="button" onClick={copy} className="px-3 border-l border-border hover:bg-secondary transition-colors flex items-center gap-1 text-xs" data-testid={`copy-${(label || "value").toLowerCase().replace(/\s+/g, "-")}`}>
          {copied ? <Check className="h-3.5 w-3.5 text-positive" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

// ---- HSL <-> HEX helpers ----
function hslStrToHex(hsl) {
  if (!hsl) return "#000000";
  const [h, s, l] = hsl.replace(/%/g, "").split(/\s+/).map(Number);
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = l / 100 - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
function hexToHslStr(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={hslStrToHex(value)} onChange={(e) => onChange(hexToHslStr(e.target.value))} className="h-9 w-12 border border-border cursor-pointer bg-transparent" data-testid={testid} />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none font-mono text-xs" />
      </div>
    </div>
  );
}

const FONTS = ["Outfit", "Manrope", "Space Grotesk", "Cabinet Grotesk", "IBM Plex Sans", "Sora", "Inter", "Poppins", "DM Sans"];

function BrandingTab() {
  const { branding, reloadBranding, logoUrl } = useBranding();
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  if (!branding) return null;
  const b = { ...branding, ...edits };
  const set = (k, v) => { const n = { ...b, [k]: v }; applyBranding(n); setEdits((e) => ({ ...e, [k]: v })); };

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/branding", b); await reloadBranding(); setEdits({}); toast.success("Branding saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/settings/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); await reloadBranding(); toast.success("Logo uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">App name</Label><Input value={b.app_name || ""} onChange={(e) => set("app_name", e.target.value)} className="rounded-none mt-1" data-testid="brand-app-name" /></div>
        <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Tagline</Label><Input value={b.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="rounded-none mt-1" data-testid="brand-tagline" /></div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Logo</Label>
        <div className="flex items-center gap-4 mt-2 border border-border p-4">
          <div className="h-16 w-16 border border-border flex items-center justify-center bg-secondary">
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-full w-full object-contain" /> : <span className="text-2xl font-heading font-bold">{(b.app_name || "M")[0]}</span>}
          </div>
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="brand-logo-upload" />
            <span className="inline-flex items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-secondary transition-colors"><Upload className="h-4 w-4" /> Upload logo</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ColorField label="Primary" value={b.primary} onChange={(v) => set("primary", v)} testid="brand-primary" />
        <ColorField label="Brand accent" value={b.accent} onChange={(v) => set("accent", v)} testid="brand-accent" />
        <ColorField label="Background" value={b.background} onChange={(v) => set("background", v)} testid="brand-bg" />
        <ColorField label="Foreground" value={b.foreground} onChange={(v) => set("foreground", v)} testid="brand-fg" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Heading font</Label>
          <Select value={b.heading_font} onValueChange={(v) => set("heading_font", v)}><SelectTrigger className="rounded-none mt-1" data-testid="brand-heading-font"><SelectValue /></SelectTrigger>
            <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Body font</Label>
          <Select value={b.body_font} onValueChange={(v) => set("body_font", v)}><SelectTrigger className="rounded-none mt-1" data-testid="brand-body-font"><SelectValue /></SelectTrigger>
            <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Corner radius</Label>
          <Select value={b.radius} onValueChange={(v) => set("radius", v)}><SelectTrigger className="rounded-none mt-1" data-testid="brand-radius"><SelectValue /></SelectTrigger>
            <SelectContent>{["0rem", "0.25rem", "0.5rem", "0.75rem", "1rem"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-none" data-testid="brand-save">{saving ? "Saving…" : "Save branding"}</Button>
    </div>
  );
}

function IntegrationsTab() {
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState("");
  const [excl, setExcl] = useState([]);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const loadExcl = () => api.get("/m365/exclusions").then((r) => setExcl(r.data)).catch(() => {});
  useEffect(() => { api.get("/settings/integration").then((r) => setS(r.data)); loadExcl(); }, []);
  if (!s) return null;

  const save = async () => {
    try { await api.put("/settings/integration", { ...s, client_secret: secret || undefined }); const r = await api.get("/settings/integration"); setS(r.data); setSecret(""); toast.success("Integration settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const restore = async (email) => {
    try { await api.post("/m365/exclusions/remove", { email }); await loadExcl(); toast.success(`${email} restored — run M365 sync to re-add them`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const restoreAll = async () => {
    try { const { data } = await api.post("/m365/exclusions/clear-all"); await loadExcl(); toast.success(`Restored ${data.removed} user(s) — run M365 sync to re-add them`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const redirectUri = `${window.location.origin}/auth/callback`;

  const guideSteps = [
    {
      t: "Step 1 — Open App registrations in Entra ID",
      body: (
        <ol className="list-decimal ml-4 space-y-1.5">
          <li>Go to <a className="text-brand underline" href="https://entra.microsoft.com" target="_blank" rel="noreferrer">entra.microsoft.com</a> and sign in as a <b>Global Administrator</b>.</li>
          <li>In the left navigation open <b>Identity → Applications → App registrations</b>.</li>
          <li>Click <b>+ New registration</b> on the top toolbar.</li>
        </ol>
      ),
    },
    {
      t: "Step 2 — Register the application",
      body: (
        <div className="space-y-2">
          <ol className="list-decimal ml-4 space-y-1.5">
            <li><b>Name:</b> type something recognisable, e.g. <span className="font-mono bg-secondary px-1">Barnard Commission Suite</span>.</li>
            <li><b>Supported account types:</b> choose <b>Accounts in this organizational directory only (Single tenant)</b>.</li>
            <li><b>Redirect URI:</b> set the platform dropdown to <b>Web</b> and paste the URI below into the box.</li>
            <li>Click <b>Register</b>.</li>
          </ol>
          <CopyBox label="Redirect URI (platform: Web)" value={redirectUri} />
        </div>
      ),
    },
    {
      t: "Step 3 — Copy the Client ID and Tenant ID",
      body: (
        <ol className="list-decimal ml-4 space-y-1.5">
          <li>After registering you land on the app&apos;s <b>Overview</b> page.</li>
          <li>Copy <b>Application (client) ID</b> → paste it into the <b>Application (Client) ID</b> field at the top of this page.</li>
          <li>Copy <b>Directory (tenant) ID</b> → paste it into the <b>Directory (Tenant) ID</b> field.</li>
        </ol>
      ),
    },
    {
      t: "Step 4 — Create a client secret",
      body: (
        <div className="space-y-2">
          <ol className="list-decimal ml-4 space-y-1.5">
            <li>In the left menu of your app, open <b>Certificates &amp; secrets</b>.</li>
            <li>Under the <b>Client secrets</b> tab click <b>+ New client secret</b>.</li>
            <li>Add a description (e.g. <span className="font-mono">commission-suite</span>) and pick an expiry (recommended <b>24 months</b>).</li>
            <li>Click <b>Add</b>. Immediately copy the <b>Value</b> column (NOT the Secret ID).</li>
            <li>Paste it into the <b>Client Secret</b> field above.</li>
          </ol>
          <p className="text-warning">⚠ The secret Value is only displayed once. If you navigate away you must create a new one.</p>
        </div>
      ),
    },
    {
      t: "Step 5 — Add API permissions (Microsoft Graph)",
      body: (
        <div className="space-y-2">
          <ol className="list-decimal ml-4 space-y-1.5">
            <li>Open <b>API permissions</b> → <b>+ Add a permission</b> → <b>Microsoft Graph</b>.</li>
            <li>Choose <b>Delegated permissions</b> (used for SSO sign-in) and add:</li>
          </ol>
          <div className="flex flex-wrap gap-1.5 ml-4">
            {["openid", "profile", "email", "offline_access", "User.Read"].map((p) => <span key={p} className="font-mono text-[11px] border border-border px-1.5 py-0.5">{p}</span>)}
          </div>
          <ol className="list-decimal ml-4 space-y-1.5" start={3}>
            <li>Add a permission again → <b>Microsoft Graph</b> → <b>Application permissions</b> (used for directory / licensed-user sync) and add:</li>
          </ol>
          <div className="flex flex-wrap gap-1.5 ml-4">
            {["User.Read.All", "Directory.Read.All"].map((p) => <span key={p} className="font-mono text-[11px] border border-border px-1.5 py-0.5">{p}</span>)}
          </div>
          <ol className="list-decimal ml-4 space-y-1.5" start={4}>
            <li>Click <b>Grant admin consent for barnardinc.co.za</b> and confirm — each row should show a green tick.</li>
          </ol>
        </div>
      ),
    },
    {
      t: "Step 6 — Paste the values here and save",
      body: (
        <ol className="list-decimal ml-4 space-y-1.5">
          <li>Scroll up on this page and confirm <b>Application (Client) ID</b>, <b>Directory (Tenant) ID</b> and <b>Client Secret</b> are filled in.</li>
          <li>Leave <b>Sync only licensed users</b> ON to import only staff with an active Microsoft 365 license.</li>
          <li>Click <b>Save integration</b>.</li>
        </ol>
      ),
    },
    {
      t: "Step 7 — Turn on Microsoft 365 sign-in",
      body: (
        <ol className="list-decimal ml-4 space-y-1.5">
          <li>Flip the <b>Microsoft 365</b> toggle at the top of this page to ON.</li>
          <li>The <b>“Continue with Microsoft 365”</b> button on the login screen becomes active.</li>
          <li>Users sign in with their <span className="font-mono">@barnardinc.co.za</span> account — no separate password needed.</li>
        </ol>
      ),
    },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="" className="h-8 w-8" />
            <div>
              <div className="font-heading font-medium">Microsoft 365</div>
              <div className="text-xs text-muted-foreground">SSO login + licensed-user directory sync</div>
            </div>
          </div>
          <Switch checked={s.m365_enabled} onCheckedChange={(v) => set("m365_enabled", v)} data-testid="m365-toggle" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Application (Client) ID</Label><Input value={s.client_id || ""} onChange={(e) => set("client_id", e.target.value)} className="rounded-none mt-1 font-mono text-xs" data-testid="m365-client-id" /></div>
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Directory (Tenant) ID</Label><Input value={s.tenant_id || ""} onChange={(e) => set("tenant_id", e.target.value)} className="rounded-none mt-1 font-mono text-xs" data-testid="m365-tenant-id" /></div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Client Secret {s.client_secret_set && <Badge variant="outline" className="ml-2 rounded-none text-[9px] text-positive border-positive/30"><Check className="h-3 w-3 mr-1" />set</Badge>}</Label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={s.client_secret_set ? "•••••••• (saved)" : "Enter secret"} className="rounded-none mt-1 font-mono text-xs" data-testid="m365-secret" />
          </div>
        </div>
        <div className="flex items-center justify-between border border-border px-3 py-2 mt-4">
          <span className="text-sm">Sync only licensed users</span>
          <Switch checked={s.sync_only_licensed} onCheckedChange={(v) => set("sync_only_licensed", v)} data-testid="m365-licensed" />
        </div>
        {s.last_sync && <p className="text-xs text-muted-foreground mt-2">Last sync: {new Date(s.last_sync).toLocaleString()}</p>}
        <Button onClick={save} className="rounded-none mt-4" data-testid="m365-save">Save integration</Button>

        <div className="mt-6 border border-border p-4" data-testid="m365-exclusions">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold">Restore deleted (tombstoned) users</h4>
              <p className="text-xs text-muted-foreground mt-0.5">People deleted in-app are kept out of every sync. Restore one to have the next sync re-add them.</p>
            </div>
            <div className="flex items-center gap-2">
              {excl.length > 0 && <Button variant="outline" size="sm" className="rounded-none text-brand" onClick={restoreAll} data-testid="m365-exclusions-restore-all">Restore all ({excl.length})</Button>}
              <Button variant="outline" size="sm" className="rounded-none" onClick={loadExcl} data-testid="m365-exclusions-refresh">Refresh</Button>
            </div>
          </div>
          {excl.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-3">No tombstoned users.</p>
          ) : (
            <ul className="mt-3 space-y-1">
              {excl.map((email) => (
                <li key={email} className="flex items-center justify-between border border-border px-3 py-1.5 text-xs">
                  <span className="font-mono">{email}</span>
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => restore(email)} data-testid={`restore-${email}`}>Restore</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm uppercase tracking-[0.1em] text-muted-foreground">Setup guide — Microsoft 365 / Entra ID</h3>
          <a href="https://entra.microsoft.com" target="_blank" rel="noreferrer" className="text-xs text-brand flex items-center gap-1 hover:underline" data-testid="entra-link">Open Entra portal <ExternalLink className="h-3 w-3" /></a>
        </div>

        <div className="border border-border bg-secondary/40 p-4 mb-4 text-xs space-y-2">
          <div className="font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">Before you begin</div>
          <p>You need a <b>Global Administrator</b> (or Application Administrator + Privileged Role Admin) account on the <span className="font-mono">barnardinc.co.za</span> tenant. The whole process takes about 5 minutes. Keep this tab open — you&apos;ll paste three values back here at the end.</p>
          <CopyBox label="Redirect URI (you&apos;ll need this in Step 2)" value={redirectUri} />
        </div>

        <Accordion type="single" collapsible defaultValue="step-0" className="border border-border" data-testid="m365-guide">
          {guideSteps.map((step, i) => (
            <AccordionItem key={i} value={`step-${i}`} className="border-b border-border last:border-0 px-4">
              <AccordionTrigger className="text-sm hover:no-underline text-left" data-testid={`guide-step-${i}`}>{step.t}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4">{step.body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="border border-warning/40 bg-warning/5 p-4 mt-4 text-xs space-y-1">
          <div className="font-semibold uppercase tracking-wide text-[10px] text-warning">Important notes</div>
          <ul className="list-disc ml-4 space-y-1 text-muted-foreground">
            <li>The client <b>secret value</b> is shown only <b>once</b> in Azure — copy it immediately. If you lose it, create a new one.</li>
            <li>Secrets <b>expire</b> (max 24 months). Set a calendar reminder to rotate it before expiry or sign-in will break.</li>
            <li>Admin consent is required for the directory-sync permissions (the green ticks under API permissions).</li>
            <li>Live Microsoft sign-in is <b>staged</b> — once your three values are saved here, we activate the real OAuth flow. Until then, use email/password.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function GlobalTab() {
  const { refreshGlobal } = useAuth();
  const [s, setS] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/settings/global").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { try { await api.put("/settings/global", s); await refreshGlobal(); toast.success("Global settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date format</Label>
          <Select value={s.date_format} onValueChange={(v) => set("date_format", v)}><SelectTrigger className="rounded-none mt-1" data-testid="global-date"><SelectValue /></SelectTrigger>
            <SelectContent>{["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Time format</Label>
          <Select value={s.time_format} onValueChange={(v) => set("time_format", v)}><SelectTrigger className="rounded-none mt-1" data-testid="global-time"><SelectValue /></SelectTrigger>
            <SelectContent>{["24h", "12h"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Timezone</Label><Input value={s.timezone} onChange={(e) => set("timezone", e.target.value)} className="rounded-none mt-1" data-testid="global-tz" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Currency symbol</Label><Input value={s.currency_symbol} onChange={(e) => set("currency_symbol", e.target.value)} className="rounded-none mt-1" data-testid="global-symbol" /></div>
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Code</Label><Input value={s.currency_code} onChange={(e) => set("currency_code", e.target.value)} className="rounded-none mt-1" data-testid="global-code" /></div>
        </div>
      </div>
      <Button onClick={save} className="rounded-none" data-testid="global-save">Save global settings</Button>
    </div>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function FiscalTab() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/settings/fiscal").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { try { await api.put("/settings/fiscal", { ...s, fiscal_year_start_month: parseInt(s.fiscal_year_start_month) }); toast.success("Fiscal settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const start = parseInt(s.fiscal_year_start_month);
  const quarters = [0, 1, 2, 3].map((q) => [0, 1, 2].map((m) => MONTHS[(start - 1 + q * 3 + m) % 12].slice(0, 3)).join(" · "));

  return (
    <div className="space-y-5 max-w-xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Fiscal year starts in</Label>
          <Select value={String(start)} onValueChange={(v) => set("fiscal_year_start_month", v)}><SelectTrigger className="rounded-none mt-1" data-testid="fiscal-start"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Current FY label</Label><Input value={s.current_fiscal_year || ""} onChange={(e) => set("current_fiscal_year", e.target.value)} className="rounded-none mt-1" data-testid="fiscal-label" /></div>
      </div>
      <div className="border border-border p-4">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-3">Period preview — enables Half / Quarter / Month filters</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quarters.map((q, i) => (
            <div key={i} className="border border-border p-3">
              <div className="font-mono text-sm font-semibold">Q{i + 1}</div>
              <div className="text-xs text-muted-foreground mt-1">{q}</div>
              <Badge variant="outline" className="rounded-none text-[9px] mt-2">H{i < 2 ? 1 : 2}</Badge>
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} className="rounded-none" data-testid="fiscal-save">Save fiscal settings</Button>
    </div>
  );
}

function CfgField({ k, label, step = "0.01", suffix, c, set }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="relative mt-1">
        <Input type="number" step={step} value={c[k]} onChange={(e) => set(k, e.target.value)} className="rounded-none font-mono pr-9" data-testid={`cfg-${k}`} />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono pointer-events-none">{suffix}</span>}
      </div>
    </div>
  );
}

const RATE_KEYS = ["standard_rate", "top_rate"];
const MULT_KEYS = ["min_multiple", "breakeven_multiple", "top_min", "cap_multiple"];

function CommissionTab() {
  const [c, setC] = useState(null);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    api.get("/commission/config").then((r) => {
      const d = { ...r.data };
      // Show rates as percentages (0.25 -> 25) so admins can't confuse 25% with 2500%.
      RATE_KEYS.forEach((k) => { d[k] = +((parseFloat(d[k]) || 0) * 100).toFixed(2); });
      setC(d);
    });
  }, []);
  if (!c) return null;

  const validate = (v) => {
    for (const k of RATE_KEYS) {
      if (isNaN(v[k]) || v[k] < 0 || v[k] > 100) return "Rates must be a percentage between 0 and 100.";
    }
    for (const k of MULT_KEYS) {
      if (isNaN(v[k]) || v[k] <= 0 || v[k] > 50) return "Multiples must be greater than 0 and at most 50×.";
    }
    if (v.breakeven_multiple < v.min_multiple) return "Break-even × cannot be below the Minimum ×.";
    if (v.top_min < v.breakeven_multiple) return "Top × cannot be below the Break-even ×.";
    const days = parseInt(v.debtor_age_days);
    if (isNaN(days) || days < 0) return "Debtor age (days) must be 0 or more.";
    return null;
  };

  const save = async () => {
    const v = { ...c };
    [...RATE_KEYS, ...MULT_KEYS].forEach((k) => (v[k] = parseFloat(v[k])));
    const err = validate(v);
    if (err) { toast.error(err); return; }
    const payload = { ...v };
    RATE_KEYS.forEach((k) => (payload[k] = +(v[k] / 100).toFixed(4))); // percent -> fraction
    payload.debtor_age_days = parseInt(payload.debtor_age_days);
    try {
      await api.put("/commission/config", payload);
      toast.success("Commission rules saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <p className="text-sm text-muted-foreground">Break-even = <b>Break-even ×</b> the quarterly basic <b>plus</b> the junior-staff salaries captured per fee earner. Commission = (Nett Fees − break-even) × rate, capped at <b>Cap ×</b> basic. Below the <b>Minimum ×</b>, no commission applies.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <CfgField k="min_multiple" label="Minimum ×" suffix="×" c={c} set={set} />
        <CfgField k="breakeven_multiple" label="Break-even ×" suffix="×" c={c} set={set} />
        <CfgField k="top_min" label="Top band ×" suffix="×" c={c} set={set} />
        <CfgField k="cap_multiple" label="Cap (× quarterly basic)" suffix="×" c={c} set={set} />
        <CfgField k="standard_rate" label="Standard rate" suffix="%" step="0.5" c={c} set={set} />
        <CfgField k="top_rate" label="Top rate (>top ×)" suffix="%" step="0.5" c={c} set={set} />
        <CfgField k="debtor_age_days" label="Debtor age" suffix="days" step="1" c={c} set={set} />
      </div>
      <Button onClick={save} className="rounded-none" data-testid="cfg-save">Save commission rules</Button>
    </div>
  );
}

function DataTab() {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeScenarios, setIncludeScenarios] = useState(true);
  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clear-financial-data", { confirm, include_scenarios: includeScenarios });
      toast.success(`Cleared ${data.total} financial record(s). Clean slate ready.`);
      setConfirm("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="data-tab">
      <div className="border border-destructive/40 bg-destructive/5 p-5">
        <div className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /><h3 className="font-heading text-sm uppercase tracking-[0.1em]">Danger zone — clear financial data</h3></div>
        <p className="text-sm text-muted-foreground mt-2">Permanently deletes <b>all banking (comm / non-comm / recovery), basic salaries, junior-staff entries, carry-in overrides, recovery labels and locked quarters</b>. Use this once, just before go-live, to start on a clean slate.</p>
        <p className="text-sm text-muted-foreground mt-2"><b>Kept:</b> users, departments, branding, fiscal, commission rules and integration settings. This cannot be undone.</p>
        <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={includeScenarios} onChange={(e) => setIncludeScenarios(e.target.checked)} data-testid="clear-scenarios" /> Also delete saved What-if scenarios</label>
        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Type CLEAR to confirm</Label>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="CLEAR" className="rounded-none mt-1 max-w-xs" data-testid="clear-confirm-input" />
        </div>
        <Button variant="destructive" disabled={busy || confirm.trim().toUpperCase() !== "CLEAR"} onClick={clear} className="rounded-none mt-4 gap-2" data-testid="clear-financial-data-btn"><Trash2 className="h-4 w-4" /> {busy ? "Clearing…" : "Clear all financial data"}</Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const tabs = [
    ["integrations", "Integrations", Plug],
    ["branding", "Branding", Palette],
    ["fiscal", "Fiscal Year", CalendarRange],
    ["global", "Global", Globe],
    ["commission", "Commission Rules", Calculator],
    ...(isAdmin ? [["data", "Data", Database]] : []),
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tighter">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Integrations, branding, fiscal configuration and commission rules.</p>
      </div>
      <Tabs defaultValue="integrations" className="flex flex-col lg:flex-row gap-6">
        <TabsList className="rounded-none h-auto flex-row lg:flex-col lg:w-56 shrink-0 bg-transparent p-0 gap-1 flex-wrap justify-start">
          {tabs.map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none w-full justify-start gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground border border-transparent data-[state=active]:border-primary px-3 py-2.5">
              <Icon className="h-4 w-4" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex-1 min-w-0">
          <TabsContent value="integrations" className="mt-0"><IntegrationsTab /></TabsContent>
          <TabsContent value="branding" className="mt-0"><BrandingTab /></TabsContent>
          <TabsContent value="fiscal" className="mt-0"><FiscalTab /></TabsContent>
          <TabsContent value="global" className="mt-0"><GlobalTab /></TabsContent>
          <TabsContent value="commission" className="mt-0"><CommissionTab /></TabsContent>
          {isAdmin && <TabsContent value="data" className="mt-0"><DataTab /></TabsContent>}
        </div>
      </Tabs>
    </div>
  );
}
